import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { hookUtils } from '@pukka/common/utils';
import { Content, Root, Trigger } from '@radix-ui/react-dropdown-menu';
import { twMerge } from 'tailwind-merge';

import { Icon } from '../../Icon';
import { Text } from '../../Text';

import { SelectOption } from './SelectOption';
import { SelectOptions } from './SelectOptions';
import { SIZES } from './state';
import { SelectProps, SelectValue, KeyType, ValueType, OptionConfig } from './types';
import {
  getKeyFrom,
  getOption,
  getOptionConfigs,
  getOriginalValueFromOption,
  getStateDefaultValue,
  getValueAsTextFromOption,
  getValueAsTextFromReference,
} from './utils';

function Select<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>(props: SelectProps<K, V, T>) {
  const {
    name,
    children,
    className,
    multiple,
    onChange,
    disabled,
    returnObject,
    icon,
    placeholder = 'Please Select',
    size = 'm',
    defaultValue,
    value,
    options,
    side = 'bottom',
  } = props;
  const sizing = SIZES[size];
  const trigger = useRef<HTMLButtonElement>(null);
  const map = useRef(new Map<string | number | boolean, OptionConfig<K, V, T>>());
  const weakMap = useRef(new WeakMap<object, OptionConfig<K, V, T>>());
  const { width } = hookUtils.useRect(trigger);
  const optionConfig = getOptionConfigs(map, weakMap, options, children);
  const stateDefaultValue = useMemo(
    () => getStateDefaultValue<K, V, T>(map.current, weakMap.current, defaultValue ?? value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultValue, value, options, children],
  );
  const [[values, valueAsText], setValues] = useState<[V[], string?]>([
    ([] as OptionConfig<K, V, T>['reference'][]).concat(stateDefaultValue ?? []).map((v) => v.key),
    getValueAsTextFromReference(stateDefaultValue),
  ]);

  const onSelectHandler = useCallback(
    (key: V, e: Event) => {
      setValues(([_values]) => {
        if (!multiple) {
          // get the data
          const option = getOption(key, map.current, weakMap.current);
          const val = getOriginalValueFromOption(option);

          if (returnObject) {
            onChange?.(val as T);
          } else {
            onChange?.(getKeyFrom<K, V, T>(val) as K);
          }

          return [[key] as V[], getValueAsTextFromOption(option)];
        }

        const index = _values.indexOf(key);

        if (index > -1) {
          _values.splice(index, 1);

          const newValues = _values.slice();
          const option = newValues.map((k) => getOption(k, map.current, weakMap.current));
          const val = option.map((o) => getOriginalValueFromOption(o)).filter((o) => o !== undefined && o !== null) as T[];

          if (returnObject) {
            onChange?.(val as T[]);
          } else {
            onChange?.(getKeyFrom<K, V, T>(val) as K[]);
          }

          return [newValues as V[], getValueAsTextFromOption(option)];
        }

        const newValues = _values.concat(key);
        const option = newValues.map((k) => getOption(k, map.current, weakMap.current));
        const val = option.map((o) => getOriginalValueFromOption(o)).filter((o) => o !== undefined && o !== null) as T[];

        if (returnObject) {
          onChange?.(val as T[]);
        } else {
          onChange?.(getKeyFrom<K, V, T>(val) as K[]);
        }

        return [newValues as V[], getValueAsTextFromOption(option)];
      });

      if (multiple) {
        e.preventDefault();
      }
    },
    [multiple, onChange, returnObject],
  );

  useEffect(() => {
    setValues([
      ([] as OptionConfig<K, V, T>['reference'][]).concat(stateDefaultValue ?? []).map((v) => v.key),
      getValueAsTextFromReference(stateDefaultValue),
    ]);
  }, [stateDefaultValue]);

  return (
    <Root>
      <Trigger
        name={name}
        disabled={disabled}
        ref={trigger}
        className={twMerge(
          'flex w-full items-center',
          'border',
          'focus-ring',
          'duration-300 ease-in-out',
          'hover:border-neutral-400',
          sizing.container,
          sizing.rounding,
          disabled ? 'border-neutral-200 bg-neutral-100 fill-neutral-300' : ' border-neutral-300 bg-white fill-neutral-400',
          className,
        )}
      >
        {icon ? <Icon icon={icon} size={sizing.icon} /> : null}
        <Text
          as="span"
          variant={sizing.variant}
          color={valueAsText === undefined ? 'neutral' : 'primary'}
          className={twMerge(sizing.content, 'w-fit truncate text-left')}
          title={valueAsText}
        >
          {valueAsText ?? placeholder}
        </Text>
        <Icon icon="chevron-down" size={sizing.icon} className="ml-auto" />
      </Trigger>
      <Content
        side={side}
        sideOffset={8}
        className={twMerge(
          'flex flex-col',
          'mt-2 max-h-56 overflow-auto',
          'gap-1',
          'bg-neutral-50',
          'shadow-md',
          sizing.rounding,
          sizing.option.container,
          'cursor-pointer focus:outline-none',
        )}
        style={{ width }}
      >
        <SelectOptions options={optionConfig} size={size} values={values} onSelect={onSelectHandler} disabled={disabled} />
      </Content>
      {/* Attach hidden input to register the data with form */}
      {name ? <input type="hidden" name={name} value={valueAsText} /> : null}
    </Root>
  );
}

Select.Option = SelectOption;

export { Select as SelectV2, type SelectValue };
